import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { canAccessRoute, getDefaultRoute, isMasterUser, getUserRoleCode } from '../../helpers/accessControl';

interface ProtectedRouteProps {
  children: React.ReactNode;
  layoutComponent?: React.ComponentType<any>;
}

/**
 * A route wrapper that enforces role-based and master-level permissions
 * Access control is based on:
 * - User.isMaster flag (system-wide master admin)
 * - UserCompanyMembership.roleId (role within current company)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  layoutComponent: LayoutComponent 
}) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // If user is not authenticated, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const companylessAllowedRoutes = new Set([
    '/select-company',
    '/create-company',
    '/subscription-checkout',
  ]);
  const hasCurrentCompany = Boolean(currentUser.currentCompanyId || currentUser.companyId);
  if (!isMasterUser(currentUser) && !hasCurrentCompany && !companylessAllowedRoutes.has(location.pathname)) {
    return <Navigate to="/select-company" replace />;
  }

  // Get user's master status
  const isMaster = isMasterUser(currentUser);
  
  // Get user's role code from their current company membership
  const userRoleCode = getUserRoleCode(currentUser);
  
  // Check if user has access to this route
  const hasAccess = canAccessRoute(isMaster, userRoleCode, location.pathname);
  
  if (!hasAccess) {
    // If the user doesn't have access, redirect to dashboard
    console.warn(
      `Access denied to ${location.pathname}. ` +
      `isMaster: ${isMaster}, roleCode: ${userRoleCode}`
    );
    const fallbackRoute = getDefaultRoute(isMaster, userRoleCode);
    return <Navigate to={fallbackRoute} replace />;
  }

  // If a layout component is provided, wrap the children in it
  if (LayoutComponent) {
    return <LayoutComponent>{children}</LayoutComponent>;
  }

  // Otherwise, just render the children
  return <>{children}</>;
};

export default ProtectedRoute;
